import clsx from 'clsx';

import { ExpertRequestOrigin, ExpertRequestSourcingScope } from '@/__generated__/graphql';
import Radio from '@/componentsv2/RadioButton/Radio';
import Switch from '@/componentsv2/Switch';
import { useAppSelector } from '@/store';

// Staff-only fields (ENG-5315). Origin and sourcing scope are enums; vetting
// authorization is a pair of booleans. Always editable — outside the §5.5 matrix.
//
// ENG-5367: the create form offers only CS/Sales-authored (no Customer-authored). The
// edit form always shows all three, all selectable, so staff can classify/re-classify.
const staffOriginOptions = [
  {
    value: ExpertRequestOrigin.CsAuthored,
    title: 'CS-authored',
    description: 'Created by OnFrontiers staff on behalf of a paying customer.',
  },
  {
    value: ExpertRequestOrigin.SalesAuthored,
    title: 'Sales-authored',
    description: 'Pre-conversion request for a prospect.',
  },
];

const customerAuthoredOption = {
  value: ExpertRequestOrigin.CustomerAuthored,
  title: 'Customer-authored',
  description: 'Created directly by the customer.',
};

const sourcingScopeOptions = [
  {
    value: ExpertRequestSourcingScope.InternalPlusExternal,
    title: 'Internal + External',
    description: 'Include experts outside the marketplace when sourcing.',
  },
  {
    value: ExpertRequestSourcingScope.InternalOnly,
    title: 'Internal only',
    description: 'Source experts from the OnFrontiers marketplace only.',
  },
];

interface SelectableCardProps {
  name: string;
  value: string;
  title: string;
  description: string;
  selected: boolean;
  onSelect: (value: string) => void;
}

function SelectableCard({
  name,
  value,
  title,
  description,
  selected,
  onSelect,
}: SelectableCardProps) {
  const controlId = `${name}-${value}`;
  return (
    <label
      htmlFor={controlId}
      className={clsx(
        'flex cursor-pointer items-start gap-8 rounded border p-16',
        selected ? 'border-brand-primary bg-brand-light' : 'border-surface-border bg-surface-card'
      )}
    >
      <Radio
        id={controlId}
        name={name}
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
        className="mt-2 !p-[0]"
      />
      <div className="flex flex-col gap-1">
        <span className="text-14 font-bold text-on-surface">{title}</span>
        <span className="text-12 text-on-surface-muted">{description}</span>
      </div>
    </label>
  );
}

interface VettingToggleProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function VettingToggle({ title, description, checked, onChange }: VettingToggleProps) {
  return (
    <div className="flex items-start justify-between gap-8">
      <div className="flex flex-col gap-1">
        <span className="text-14 font-bold text-on-surface">{title}</span>
        <span className="text-12 text-on-surface-muted">{description}</span>
      </div>
      <Switch
        checked={checked}
        onChange={(_, value) => onChange(value)}
        inputProps={{ 'aria-label': title }}
      />
    </div>
  );
}

interface InternalErFieldsProps {
  values: Record<string, any>;
  change: (field: string, value: any) => void;
  // Create form shows only CS/Sales; the edit form (default) always shows all three.
  isCreate?: boolean;
}

export function InternalErFields({ values, change, isCreate = false }: InternalErFieldsProps) {
  const isViewerAdmin = useAppSelector((s) => s.viewer.admin);

  if (!isViewerAdmin) {
    return null;
  }

  // ENG-5367: create form shows only CS/Sales; edit form always shows all three.
  const originOptions = isCreate
    ? staffOriginOptions
    : [customerAuthoredOption, ...staffOriginOptions];

  return (
    <div className="mt-16 flex flex-col gap-16 rounded border border-surface-border bg-surface-card p-16">
      <div className="flex flex-col gap-8">
        <span className="text-center text-14 font-bold uppercase tracking-widest text-on-surface-muted">
          INTERNAL · STAFF ONLY
        </span>
        <span className="text-14 font-bold text-on-surface">ER origin</span>
        {originOptions.map((option) => (
          <SelectableCard
            key={option.value}
            name="origin"
            value={option.value}
            title={option.title}
            description={option.description}
            selected={values.origin === option.value}
            onSelect={(value) => change('origin', value)}
          />
        ))}
      </div>

      <hr className="border-surface-border" />

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1">
          <span className="text-14 font-bold text-on-surface">Sourcing scope</span>
          <span className="text-12 text-on-surface-muted">Which expert pool to source from.</span>
        </div>
        {sourcingScopeOptions.map((option) => (
          <SelectableCard
            key={option.value}
            name="sourcing_scope"
            value={option.value}
            title={option.title}
            description={option.description}
            selected={values.sourcing_scope === option.value}
            onSelect={(value) => change('sourcing_scope', value)}
          />
        ))}
      </div>

      <hr className="border-surface-border" />

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1">
          <span className="text-14 font-bold text-on-surface">Vetting authorization</span>
          <span className="text-12 text-on-surface-muted">
            Two independent permissions controlling outreach to sourced experts.
          </span>
        </div>
        <VettingToggle
          title="Allow internal vetting"
          description="Reach out to vet marketplace experts."
          checked={!!values.allow_internal_vetting}
          onChange={(checked) => change('allow_internal_vetting', checked)}
        />
        <VettingToggle
          title="Allow external recruiting"
          description="Reach out to recruit non-marketplace experts."
          checked={!!values.allow_external_recruiting}
          onChange={(checked) => change('allow_external_recruiting', checked)}
        />
      </div>
    </div>
  );
}

export default InternalErFields;
